/**
 * O que mudou na plataforma, para o aviso que abre quando a pessoa entra.
 *
 * ## ⚠ Como manter isto
 *
 * **Ao entregar algo que a pessoa vai notar, acrescente uma entrada aqui, com
 * `versao` maior que a última.** Um aviso que não acompanha a entrega é pior do
 * que nenhum: ele ensina a ignorar a janela.
 *
 * Não listo mudança interna — correção de teste, refatoração, ajuste de
 * infraestrutura. O critério é simples: **se a pessoa não notaria usando a
 * plataforma, não entra.** A janela compete com o trabalho dela.
 *
 * ## Por que a lista é código, e não cadastro
 *
 * Porque ela é um fato sobre a versão que está rodando. Num cadastro, daria
 * para anunciar o que ainda não subiu, ou esquecer de anunciar o que subiu — e
 * divergir em silêncio. Mudar o texto exige deploy, e isso não é atrito: o
 * aviso existe justamente para anunciar um deploy.
 *
 * Mesma razão de `tour/passos.js` ser código.
 *
 * ## `versao` é um contador de ENTREGAS, não de itens
 *
 * Tudo o que sobe junto tem a mesma `versao`. O banco guarda, por pessoa, a
 * maior versão que ela fechou; a janela mostra o que estiver acima disso.
 *
 * ## `publico` separa quem vê
 *
 * `'todos'` aparece para qualquer pessoa. `'admin'` só para administradores — e
 * não aparece apagado para os demais: **não existe** para eles, inclusive na
 * hora de decidir se a janela abre. Uma entrega só de administração não
 * interrompe ninguém mais.
 */

/** Um item por mudança que a pessoa nota. O mais novo primeiro. */
export const NOVIDADES = Object.freeze([
  {
    id: 'aviso-de-novidades',
    versao: 3,
    data: '2026-10-26',
    publico: 'todos',
    titulo: 'Esta janela: agora a plataforma avisa o que mudou',
    texto:
      'Sempre que algo novo entrar, você vê aqui ao entrar — uma vez só, e some depois que você fecha. Para reler, use “O que há de novo” no menu do seu nome.',
  },
  {
    id: 'ultimo-acesso-real',
    versao: 3,
    data: '2026-10-26',
    publico: 'admin',
    titulo: '“Último acesso” passou a ser a última vez que a pessoa usou',
    texto:
      'A coluna mostrava o instante do login, e com sessão de horas as duas coisas ficavam longe uma da outra. Agora ela mostra a atividade; o login continua no texto que aparece ao passar o cursor.',
  },
  {
    id: 'quem-esta-online',
    versao: 3,
    data: '2026-10-26',
    publico: 'admin',
    titulo: 'A lista de usuários mostra quem está usando agora',
    texto:
      'Um marcador verde na linha de quem deu sinal nos últimos 3 minutos, e a contagem no topo. Atualiza sozinho a cada 30 segundos enquanto a aba estiver à frente.',
  },
  {
    id: 'artefatos-abertos-a-todos',
    versao: 2,
    data: '2026-10-26',
    publico: 'todos',
    titulo: 'Os artefatos abertos a todos agora aparecem para quem ainda não publicou',
    texto:
      'Quem nunca publicou um artefato via uma tela vazia, mesmo havendo artefatos abertos a toda a companhia. Agora eles aparecem de saída, na aba “Abertos a todos”.',
  },
  {
    id: 'sem-piscar-no-login',
    versao: 2,
    data: '2026-10-26',
    publico: 'todos',
    titulo: 'A área logada não pisca mais antes da tela de entrada',
    texto:
      'Abrir a plataforma sem estar logado mostrava a área interna por um instante antes de redirecionar. O endereço agora leva direto à entrada.',
  },
  {
    id: 'abas-gerenciaveis',
    versao: 1,
    data: '2026-10-25',
    publico: 'admin',
    titulo: 'As abas do portfólio viraram cadastro',
    texto:
      'Em Administração → Abas dá para criar, renomear, reordenar, desligar e apagar as abas do portfólio. Desligar tira a aba da barra e guarda os vínculos; apagar desfaz os vínculos.',
  },
  {
    id: 'bloqueio-de-solicitacao',
    versao: 1,
    data: '2026-10-25',
    publico: 'admin',
    titulo: 'Ferramenta de uso fechado pode recusar solicitação',
    texto:
      'Na edição do item há uma opção para não aceitar solicitação de acesso, com um campo de motivo que aparece no lugar do botão. Fecha o pedido, não o uso de quem já tem acesso.',
  },
  {
    id: 'endereco-com-nome',
    versao: 1,
    data: '2026-10-25',
    publico: 'todos',
    titulo: 'O endereço de um artefato agora traz o nome dele',
    texto:
      'Em vez de um código aleatório, o link de um artefato passa a ser o nome: /pagina-painel-de-vendas. Os endereços antigos continuam abrindo.',
  },
  {
    id: 'barra-do-topo',
    versao: 1,
    data: '2026-10-25',
    publico: 'todos',
    titulo: 'A barra do topo ficou mais visível',
    texto:
      'Agentes, Sistemas, Artefatos e Solicitações ganharam mais destaque, e a seção em que você está fica marcada.',
  },
]);

/** A entrega mais recente. É o que o ponteiro de quem fecha a janela recebe. */
export const VERSAO_ATUAL = NOVIDADES.reduce((maior, n) => Math.max(maior, n.versao), 0);

/**
 * O que ESTA pessoa ainda não viu.
 *
 * `versaoVista` nulo é quem nunca fechou a janela: vê tudo o que há, porque
 * para ela o aviso está estreando. Não é o mesmo que a primeira visita à
 * plataforma — essa não vê nada, e quem decide isso é quem chama.
 */
export function novidadesPara({ versaoVista, admin }) {
  const piso = versaoVista ?? 0;
  return NOVIDADES.filter((n) => n.versao > piso).filter((n) => n.publico === 'todos' || admin);
}

/**
 * Agrupa por entrega, da mais nova para a mais antiga.
 *
 * A janela mostra por data e não em lista corrida porque "o que mudou" se lê
 * em levas: três itens de ontem e dois da semana passada são duas notícias,
 * não cinco.
 */
export function porEntrega(itens) {
  const mapa = new Map();
  for (const n of itens) {
    if (!mapa.has(n.versao)) mapa.set(n.versao, { versao: n.versao, data: n.data, itens: [] });
    mapa.get(n.versao).itens.push(n);
  }
  return [...mapa.values()].sort((a, b) => b.versao - a.versao);
}

export default { NOVIDADES, VERSAO_ATUAL, novidadesPara, porEntrega };
